/**
 * Shared lead ↔ listing link editor (B2).
 *
 * Both edit surfaces route their link/unlink writes through this hook so
 * the two paths can't diverge in behaviour:
 *   - `pages/listing-detail.tsx` — "Interested buyers": add/remove a lead
 *     for a fixed listing (`linkOne` / `unlinkOne`, one lead at a time).
 *   - `pages/lead-form.tsx` — the listings multi-select: after
 *     `updateLead` succeeds, reconcile the selected-listings diff for a
 *     fixed lead (`reconcileLeadListings`).
 *
 * `linkListingToLead` / `unlinkListingFromLead` both return
 * `ApiResult<null>` (no server echo), so callers reconcile optimistic
 * state by re-fetching rather than trusting a returned row. This hook
 * owns only the write calls + the diff maths; the optimistic UI state
 * (and its rollback) lives in each page, close to that page's render.
 */

import { useCallback } from "react";
import {
	type ApiResult,
	linkListingToLead,
	unlinkListingFromLead,
} from "@/features/realtor/services/leads-api.service";

/** A single link/unlink call that failed during a reconcile fan-out. */
export interface LinkFailure {
	listingId: string;
	action: "link" | "unlink";
	error: string;
}

export interface ReconcileResult {
	ok: boolean;
	failures: LinkFailure[];
}

export interface UseListingLinks {
	/** Link one lead to one listing. */
	linkOne: (leadId: string, listingId: string) => Promise<ApiResult<null>>;
	/** Unlink one lead from one listing. */
	unlinkOne: (leadId: string, listingId: string) => Promise<ApiResult<null>>;
	/**
	 * Reconcile a lead's linked-listing set from `currentIds` to
	 * `targetIds` by linking the additions and unlinking the removals.
	 *
	 * NON-ATOMIC: the backend has no bulk endpoint, so this fans out one
	 * call per changed listing. Every failure is collected (the fan-out
	 * does NOT stop on the first error) so the caller can name exactly
	 * which listings didn't save. `ok` is true only when every call
	 * succeeded.
	 */
	reconcileLeadListings: (
		leadId: string,
		currentIds: string[],
		targetIds: string[],
	) => Promise<ReconcileResult>;
}

export function useListingLinks(): UseListingLinks {
	const linkOne = useCallback(
		(leadId: string, listingId: string) => linkListingToLead(leadId, listingId),
		[],
	);

	const unlinkOne = useCallback(
		(leadId: string, listingId: string) =>
			unlinkListingFromLead(leadId, listingId),
		[],
	);

	const reconcileLeadListings = useCallback(
		async (
			leadId: string,
			currentIds: string[],
			targetIds: string[],
		): Promise<ReconcileResult> => {
			const currentSet = new Set(currentIds);
			const targetSet = new Set(targetIds);
			const toAdd = targetIds.filter((id) => !currentSet.has(id));
			const toRemove = currentIds.filter((id) => !targetSet.has(id));

			const failures: LinkFailure[] = [];

			// Fan out each phase concurrently. Every call is awaited (via
			// Promise.all) regardless of siblings' outcomes, so a failure
			// never short-circuits the others — the "collect every failure"
			// contract holds. Add-then-remove phase ordering is preserved.
			await Promise.all(
				toAdd.map(async (listingId) => {
					const result = await linkListingToLead(leadId, listingId);
					if (!result.success) {
						failures.push({
							listingId,
							action: "link",
							error: result.error ?? "Failed to link listing",
						});
					}
				}),
			);
			await Promise.all(
				toRemove.map(async (listingId) => {
					const result = await unlinkListingFromLead(leadId, listingId);
					if (!result.success) {
						failures.push({
							listingId,
							action: "unlink",
							error: result.error ?? "Failed to unlink listing",
						});
					}
				}),
			);

			return { ok: failures.length === 0, failures };
		},
		[],
	);

	return { linkOne, unlinkOne, reconcileLeadListings };
}
